/**
 * RunwayStrip.jsx — cash position, burn, and runway.
 *
 * Cash and card debt come from live bank balances; receivables come from the
 * portal's own invoices. Burn is trailing whole months of real outflow
 * (transfers and card payments excluded server-side).
 */
import { Box, Paper, Typography, Tooltip, Skeleton } from '@mui/material';
import { fmtC } from '../../lib/utils';
import { useBankRunway } from '../../hooks/usePlaid';

function Tile({ label, value, hint, accent }) {
  return (
    <Paper sx={{ p: 2, flex: '1 1 160px', minWidth: 150 }}>
      <Tooltip title={hint || ''} placement="top" arrow disableHoverListener={!hint}>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, color: accent || 'text.primary', lineHeight: 1.2 }}>
            {value}
          </Typography>
        </Box>
      </Tooltip>
    </Paper>
  );
}

export default function RunwayStrip({ enabled = true }) {
  const { data, isLoading } = useBankRunway(3, enabled);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rounded" height={80} sx={{ flex: '1 1 160px', minWidth: 150 }} />
        ))}
      </Box>
    );
  }
  if (!data) return null;

  const runway = data.runwayMonths;

  return (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
      <Tile
        label="Cash"
        value={fmtC(data.cash)}
        hint="Current balance across connected checking and savings accounts."
      />
      <Tile
        label="Card balance"
        value={fmtC(data.cardDebt)}
        accent={data.cardDebt > 0 ? 'warning.main' : undefined}
        hint="What's currently owed on connected credit cards."
      />
      <Tile
        label="Net cash"
        value={fmtC(data.netCash)}
        accent={data.netCash < 0 ? 'error.main' : 'success.main'}
        hint="Cash minus card balance."
      />
      <Tile
        label="Monthly burn"
        value={fmtC(data.monthlyBurn)}
        hint={`Average real outflow over the last ${data.burnMonthsSampled} whole months. Transfers between your own accounts and credit card payments are excluded so they aren't counted twice.`}
      />
      <Tile
        label="Runway"
        // A null burn means no spend has been recorded yet — saying "∞ months"
        // would read as a healthy result rather than an empty dataset.
        value={runway == null ? '—' : `${runway.toFixed(1)} mo`}
        accent={runway != null && runway < 3 ? 'error.main' : undefined}
        hint={runway == null
          ? 'Not enough completed months of spend to estimate burn yet.'
          : 'Net cash divided by monthly burn. Does not count money still owed to you.'}
      />
      <Tile
        label="Owed to you"
        value={fmtC(data.outstandingReceivables)}
        hint="Unpaid invoice balances in the portal. Not included in runway."
      />
    </Box>
  );
}
